import { Injectable, signal } from '@angular/core';

const KEY_CUSTOMER_ACCESS = 'pryspace:customer:access';
const KEY_CUSTOMER_REFRESH = 'pryspace:customer:refresh';

/**
 * Customer-only token store.
 *
 * The backend was simplified to two audiences (customer + internal); pryspace
 * never sees an `internal` token, so we only hold the customer pair here.
 * Tenant-scoping is enforced server-side from the slug in the URL — there is
 * no tenant-audience token to manage.
 *
 * On construction we sweep any leftover `pryspace:tenant:*` keys from the
 * previous two-token model so old logins don't carry corpses around.
 */
@Injectable({ providedIn: 'root' })
export class TokenStore {
  readonly customerAccess = signal<string | null>(
    typeof localStorage !== 'undefined' ? localStorage.getItem(KEY_CUSTOMER_ACCESS) : null,
  );

  constructor() {
    this.purgeLegacyTenantKeys();
  }

  setCustomerTokens(access: string, refresh: string): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(KEY_CUSTOMER_ACCESS, access);
    localStorage.setItem(KEY_CUSTOMER_REFRESH, refresh);
    this.customerAccess.set(access);
  }

  getCustomerAccess(): string | null {
    return typeof localStorage !== 'undefined'
      ? localStorage.getItem(KEY_CUSTOMER_ACCESS)
      : null;
  }

  getCustomerRefresh(): string | null {
    return typeof localStorage !== 'undefined'
      ? localStorage.getItem(KEY_CUSTOMER_REFRESH)
      : null;
  }

  clearCustomer(): void {
    if (typeof localStorage === 'undefined') {
      this.customerAccess.set(null);
      return;
    }
    localStorage.removeItem(KEY_CUSTOMER_ACCESS);
    localStorage.removeItem(KEY_CUSTOMER_REFRESH);
    this.customerAccess.set(null);
  }

  clearAll(): void {
    this.clearCustomer();
    this.purgeLegacyTenantKeys();
  }

  // ===== JWT inspection (no signature check, just decode) =====
  decode(token: string | null): Record<string, unknown> | null {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  isExpired(token: string | null, skewSeconds = 30): boolean {
    const claims = this.decode(token);
    const exp = claims?.['exp'];
    if (typeof exp !== 'number') return true;
    return Date.now() / 1000 + skewSeconds >= exp;
  }

  /** Returns the decoded payload of the current customer access token, if any. */
  customerClaims(): Record<string, unknown> | null {
    return this.decode(this.getCustomerAccess());
  }

  private purgeLegacyTenantKeys(): void {
    if (typeof localStorage === 'undefined') return;
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('pryspace:tenant:')) stale.push(k);
    }
    stale.forEach((k) => localStorage.removeItem(k));
  }
}
